import { useCallback } from "react";
import { BigNumberish } from "ethers";
import { useQuery } from "@tanstack/react-query";
import { RaffleV2Abi } from "@looksrare/config";
import { NoPublicClientError, RQueryOptions, useAddressesByNetwork } from "@looksrare/utils";
import { useAccount, usePublicClient, useWalletClient } from "wagmi";
import { getAddress } from "viem";
import {
  ClaimRafflePrizeCalldata,
  ClaimRafflePrizesCalldata,
  EntryCalldata,
  Raffle,
  RaffleCreationCallData,
  RaffleStatus,
} from "views/win/userGeneratedRaffles/types";

const useRaffleV2ConractInfo = () => {
  const addressesByNetwork = useAddressesByNetwork();
  return {
    address: getAddress(addressesByNetwork.RAFFLE_V2),
    abi: RaffleV2Abi,
  } as const;
};

export const useEnterRaffles = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (entries: EntryCalldata[], value?: BigNumberish) => {
      if (!walletClient) {
        throw Error("No wallet client found");
      }
      if (!publicClient) {
        throw new NoPublicClientError();
      }

      // Convert call data to match the contract method signature
      const entriesViem = entries.map((entry) => {
        const { raffleId, pricingOptionIndex, count, recipient } = entry;
        return {
          raffleId: BigInt(raffleId.toString()),
          pricingOptionIndex: BigInt(pricingOptionIndex.toString()),
          count: Number(count),
          recipient: getAddress(recipient),
        };
      });

      const [account] = await walletClient.getAddresses();
      const { request } = await publicClient.simulateContract({
        ...raffleV2ContractInfo,
        functionName: "enterRaffles",
        args: [entriesViem],
        value: value ? BigInt(value.toString()) : undefined,
        account,
      });
      return walletClient.writeContract(request);
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};

export const useClaimRafflePrizes = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (claimPrizesCalldataArray: ClaimRafflePrizesCalldata[]) => {
      if (walletClient && publicClient) {
        const [account] = await walletClient.getAddresses();

        // Convert call data to match the contract method signature
        const claimPrizesCalldataArrayViem = claimPrizesCalldataArray.map((claimPrizesCalldata) => {
          const { raffleId, winnerIndices } = claimPrizesCalldata;
          return {
            raffleId: BigInt(raffleId.toString()),
            winnerIndices: winnerIndices.map((winnerIndex) => BigInt(winnerIndex.toString())),
          };
        });

        const { request } = await publicClient.simulateContract({
          ...raffleV2ContractInfo,
          functionName: "claimPrizes",
          args: [claimPrizesCalldataArrayViem],
          account,
        });
        return walletClient.writeContract(request);
      }
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};

export const useClaimRafflePrize = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (claimPrizeCalldata: ClaimRafflePrizeCalldata) => {
      if (walletClient && publicClient) {
        const [account] = await walletClient.getAddresses();
        const { request } = await publicClient.simulateContract({
          ...raffleV2ContractInfo,
          functionName: "claimPrize",
          args: [BigInt(claimPrizeCalldata.raffleId.toString()), BigInt(claimPrizeCalldata.winnerIndex.toString())],
          account,
        });
        return walletClient.writeContract(request);
      }
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};

export const useUserRaffleRefundStats = (onChainId: BigNumberish, raffleStatus: Raffle["status"]) => {
  const { address } = useAccount();
  const publicClient = usePublicClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useQuery({
    queryKey: ["has-claimed-refund", onChainId.toString(), address],
    queryFn: async () => {
      if (!publicClient) {
        throw new NoPublicClientError();
      }

      const [spent, entriesCount, refunded] = await publicClient.readContract({
        ...raffleV2ContractInfo,
        functionName: "rafflesParticipantsStats",
        args: [BigInt(onChainId.toString()), address!],
      });
      const isRefundableState = raffleStatus === RaffleStatus.CANCELLED || raffleStatus === RaffleStatus.REFUNDABLE;
      return { canClaim: entriesCount !== 0 && refunded === false && isRefundableState, value: spent };
    },
    enabled: !!address,
  });
};

export const useClaimRaffleRefund = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (raffleId: BigNumberish) => {
      if (walletClient && publicClient) {
        const [account] = await walletClient.getAddresses();
        const { request } = await publicClient.simulateContract({
          ...raffleV2ContractInfo,
          functionName: "claimRefund",
          args: [[BigInt(raffleId.toString())]],
          account,
        });
        return walletClient.writeContract(request);
      }
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};

export const useCreateRaffle = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (raffleCreationData: RaffleCreationCallData & never[]) => {
      if (!walletClient) {
        throw Error("No wallet client found");
      }
      if (!publicClient) {
        throw new NoPublicClientError();
      }

      const [account] = await walletClient.getAddresses();

      // Convert call data to match the contract method signature
      const raffleCreationDataViem = {
        ...raffleCreationData,
        pricingOptions: raffleCreationData.pricingOptions.map((pricingOption) => ({
          ...pricingOption,
          price: BigInt(pricingOption.price),
        })),
      };

      const { request } = await publicClient.simulateContract({
        ...raffleV2ContractInfo,
        functionName: "createRaffle",
        args: [raffleCreationDataViem],
        account,
      });

      // @note Wagmi v1 --> v2
      // eslint-disable-next-line  @typescript-eslint/ban-ts-comment
      // @ts-ignore next-line
      return walletClient.writeContract(request);
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};

export const useRaffleFeesBps = (onSuccess?: (protocolFeeBp: number) => void, options?: RQueryOptions<number>) => {
  const { data: walletClient } = useWalletClient();
  const publicClient = usePublicClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useQuery({
    queryKey: ["raffle-fees"],
    queryFn: async () => {
      if (!walletClient) {
        throw Error("No wallet client found");
      }
      if (!publicClient) {
        throw new NoPublicClientError();
      }

      const [account] = await walletClient.getAddresses();
      const protocolFeeBp = await publicClient.readContract({
        ...raffleV2ContractInfo,
        functionName: "protocolFeeBp",
        account,
      });
      onSuccess?.(protocolFeeBp);
      return protocolFeeBp;
    },
    ...options,
    enabled: !!walletClient && options?.enabled,
  });
};

export const useCancelRaffleV2 = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (raffleId: BigNumberish) => {
      if (!walletClient) {
        throw Error("No wallet client found");
      }
      if (!publicClient) {
        throw new NoPublicClientError();
      }

      const [account] = await walletClient.getAddresses();
      const { request } = await publicClient.simulateContract({
        ...raffleV2ContractInfo,
        functionName: "cancel",
        args: [BigInt(raffleId.toString())],
        account,
      });
      return walletClient.writeContract(request);
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};

export const useWithdrawPrizesV2 = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (raffleId: BigNumberish) => {
      if (!walletClient) {
        throw Error("No wallet client found");
      }
      if (!publicClient) {
        throw new NoPublicClientError();
      }

      const [account] = await walletClient.getAddresses();
      const { request } = await publicClient.simulateContract({
        ...raffleV2ContractInfo,
        functionName: "withdrawPrizes",
        args: [BigInt(raffleId.toString())],
        account,
      });
      return walletClient.writeContract(request);
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};

export const useDrawWinnersV2 = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (raffleId: BigNumberish) => {
      if (!walletClient) {
        throw Error("No wallet client found");
      }
      if (!publicClient) {
        throw new NoPublicClientError();
      }

      const [account] = await walletClient.getAddresses();
      const { request } = await publicClient.simulateContract({
        ...raffleV2ContractInfo,
        functionName: "drawWinners",
        args: [BigInt(raffleId.toString())],
        account,
      });
      return walletClient.writeContract(request);
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};

export const useClaimProceedsV2 = () => {
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const raffleV2ContractInfo = useRaffleV2ConractInfo();

  return useCallback(
    async (raffleId: BigNumberish) => {
      if (!walletClient) {
        throw Error("No wallet client found");
      }
      if (!publicClient) {
        throw new NoPublicClientError();
      }

      const [account] = await walletClient.getAddresses();
      const { request } = await publicClient.simulateContract({
        ...raffleV2ContractInfo,
        functionName: "claimFees",
        args: [BigInt(raffleId.toString())],
        account,
      });
      return walletClient.writeContract(request);
    },
    [publicClient, walletClient, raffleV2ContractInfo]
  );
};
